// TODO: i18n - receive translations parameter for multilingual PDF generation
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Criticality label/color mappings (mirror Reports.jsx constants)
 */
const CRIT_LABELS = { MA: 'Very High', A: 'High', M: 'Moderate', B: 'Low', MB: 'Very Low' };
const CRIT_HEX = { MA: '#dc2626', A: '#f97316', M: '#eab308', B: '#3b82f6', MB: '#22c55e' };
const RISK_HEX = { Critical: '#dc2626', High: '#f97316', Moderate: '#eab308', Low: '#3b82f6', Info: '#94a3b8' };

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}

/**
 * Generate a branded CII Assessment Report PDF.
 *
 * @param {Object} data          - Report summary from /admin/reports/summary
 * @param {Object} [orgOverride] - Optional organization override (if fetched separately)
 * @param {Object} [options] - Optional settings: { anonymized: boolean }
 * @returns {void} triggers browser download
 */
export default async function generateReportPdf(data, orgOverride, options = {}, labels = {}) {
  const L = {
    headerTitle: 'CII Security Management',
    reportTitle: 'CII Assessment Report',
    reportFilenamePrefix: 'CII',
    platformTitle: 'Critical Information Infrastructure',
    entityName: 'OES',
    entityNamePlural: 'OES',
    brandName: 'LAC4',
    ...labels,
  };
  const org = orgOverride || data.organization || {};
  const isAnonymized = options.anonymized || false;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentW = pageW - margin * 2;
  let y = margin;

  // ── Helpers ──────────────────────────────────────────────────────────
  const addPage = () => {
    doc.addPage();
    y = margin;
    drawFooter();
  };

  const checkSpace = (needed) => {
    if (y + needed > pageH - 20) addPage();
  };

  const drawFooter = () => {
    // Placeholder — actual footers with correct page numbers are added in the final loop
  };

  const sectionTitle = (text) => {
    checkSpace(14);
    doc.setFontSize(13);
    doc.setTextColor(30, 41, 59); // slate-800
    doc.setFont(undefined, 'bold');
    doc.text(text, margin, y);
    y += 2;
    doc.setDrawColor(226, 232, 240); // slate-200
    doc.setLineWidth(0.4);
    doc.line(margin, y, pageW - margin, y);
    y += 6;
    doc.setFont(undefined, 'normal');
  };

  const kpiBox = (label, value, x, w) => {
    doc.setFillColor(248, 250, 252); // slate-50
    doc.roundedRect(x, y, w, 16, 2, 2, 'F');
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184); // slate-400
    doc.text(label.toUpperCase(), x + w / 2, y + 5, { align: 'center' });
    doc.setFontSize(14);
    doc.setTextColor(30, 41, 59);
    doc.setFont(undefined, 'bold');
    doc.text(String(value), x + w / 2, y + 13, { align: 'center' });
    doc.setFont(undefined, 'normal');
  };

  // ── Cover / Header ──────────────────────────────────────────────────
  // Try to add organization logo
  let logoAdded = false;
  if (org.logo) {
    try {
      const logoSrc = org.logo;
      // Determine image format from data URL or default to PNG
      let fmt = 'PNG';
      if (logoSrc.startsWith('data:image/jpeg') || logoSrc.startsWith('data:image/jpg')) fmt = 'JPEG';
      else if (logoSrc.startsWith('data:image/png')) fmt = 'PNG';
      // Use a contained box that preserves aspect ratio (wider than tall)
      doc.addImage(logoSrc, fmt, margin, y, 22, 14);
      logoAdded = true;
    } catch {
      // Logo failed to load — continue without it
    }
  }

  if (logoAdded) {
    doc.setFontSize(16);
    doc.setTextColor(30, 41, 59);
    doc.setFont(undefined, 'bold');
    doc.text(org.name || '', margin + 26, y + 6);
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.setFont(undefined, 'normal');
    if (org.website) doc.text(org.website, margin + 26, y + 11);
    y += 18;
  } else {
    doc.setFontSize(18);
    doc.setTextColor(30, 41, 59);
    doc.setFont(undefined, 'bold');
    doc.text(org.name || L.headerTitle, margin, y + 8);
    y += 14;
    doc.setFont(undefined, 'normal');
  }

  // Report title
  doc.setFontSize(16);
  doc.setTextColor(30, 41, 59);
  doc.setFont(undefined, 'bold');
  const titleText = data.assessment_name
    ? `${L.reportTitle}: ${data.assessment_name}`
    : L.reportTitle;
  doc.text(titleText, margin, y + 6);
  y += 12;

  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.setFont(undefined, 'normal');
  doc.text(`${L.platformTitle} Risk Assessment`, margin, y);
  y += 4;
  doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, margin, y);
  y += 4;
  if (isAnonymized) {
    doc.setFontSize(8);
    doc.setTextColor(220, 38, 38); // red-600
    doc.text(`Note: ${L.entityName} identities have been anonymized in this report.`, margin, y);
    y += 4;
  }
  y += 4;

  // Divider
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);
  y += 10;

  drawFooter();

  // ── 1. Executive Summary ────────────────────────────────────────────
  sectionTitle('Executive Summary');

  // KPI cards row (6 cards in 2 rows of 3)
  const kpiW = (contentW - 6) / 3;
  const kpis = [
    ['Assessments', data.total_assessments || 0],
    [`${L.entityName} Profiled`, data.total_oes || 0],
    ['Avg Compliance', `${data.average_compliance || 0}%`],
    ['Criticality Assessed', data.assessed_criticality || 0],
    ['Posture Assessed', data.assessed_posture || 0],
    ['Max Risk Score', data.max_risk_score || 0],
  ];
  kpis.forEach((kpi, i) => {
    const col = i % 3;
    const x = margin + col * (kpiW + 3);
    kpiBox(kpi[0], kpi[1], x, kpiW);
    if (col === 2) y += 20;
  });
  if (kpis.length % 3 !== 0) y += 20;
  y += 2;

  // Sector distribution table
  if ((data.sector_distribution || []).length > 0) {
    checkSpace(30);
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text('SECTOR DISTRIBUTION', margin, y);
    y += 4;
    autoTable(doc, {
      startY: y,
      head: [['Sector', `${L.entityName} Count`]],
      body: data.sector_distribution.map(s => [s.sector, s.count]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [241, 245, 249], textColor: [71, 85, 105], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── 2. Criticality Assessment ───────────────────────────────────────
  sectionTitle('Criticality Assessment');

  // Distribution summary
  if ((data.criticality_distribution || []).length > 0) {
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text('CRITICALITY DISTRIBUTION', margin, y);
    y += 4;

    // Horizontal mini-bars
    data.criticality_distribution.forEach(d => {
      checkSpace(8);
      const label = CRIT_LABELS[d.level] || d.level;
      const color = hexToRgb(CRIT_HEX[d.level] || '#94a3b8');
      const maxCount = Math.max(...data.criticality_distribution.map(x => x.count), 1);
      const barW = (d.count / maxCount) * (contentW - 45);

      doc.setFontSize(8);
      doc.setTextColor(71, 85, 105);
      doc.text(label, margin, y + 4);
      doc.setFillColor(...color);
      doc.roundedRect(margin + 35, y, Math.max(barW, 2), 5, 1, 1, 'F');
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      doc.text(String(d.count), margin + 38 + barW, y + 4);
      y += 8;
    });
    y += 4;
  }

  // OES Criticality table
  if ((data.oes_criticality_rows || []).length > 0) {
    checkSpace(20);
    autoTable(doc, {
      startY: y,
      head: [[`${L.entityName} Name`, 'Sector', 'Criticality', 'Impact', 'Automation', 'Date']],
      body: data.oes_criticality_rows.map(r => [
        r.oes_name,
        r.sector || '—',
        r.criticality ? (CRIT_LABELS[r.criticality.toUpperCase()] || r.criticality) : 'Pending',
        r.impact_level ? (CRIT_LABELS[r.impact_level.toUpperCase()] || r.impact_level) : '—',
        r.automation_level ? (CRIT_LABELS[r.automation_level.toUpperCase()] || r.automation_level) : '—',
        r.assessment_date ? new Date(r.assessment_date).toLocaleDateString() : '—',
      ]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, cellPadding: 2 },
      headStyles: { fillColor: [241, 245, 249], textColor: [71, 85, 105], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      didParseCell: (hookData) => {
        // Color the Criticality column
        if (hookData.section === 'body' && hookData.column.index === 2) {
          const val = (data.oes_criticality_rows[hookData.row.index]?.criticality || '').toUpperCase();
          const hex = CRIT_HEX[val];
          if (hex) hookData.cell.styles.textColor = hexToRgb(hex);
        }
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── 3. Posture & Compliance ─────────────────────────────────────────
  sectionTitle('Posture & Compliance');

  // Avg compliance KPI
  checkSpace(22);
  const compKpiW = (contentW - 3) / 2;
  kpiBox('Average Compliance', `${data.average_compliance || 0}%`, margin, compKpiW);
  kpiBox(`${L.entityName} Assessed`, `${data.assessed_posture || 0} / ${data.total_oes || 0}`, margin + compKpiW + 3, compKpiW);
  y += 20;

  // Average by function
  const avgByFn = data.average_by_function || {};
  const fnOrder = Object.keys(avgByFn).length > 0 ? Object.keys(avgByFn) : ['Govern', 'Identify', 'Protect', 'Detect', 'Respond', 'Recover'];
  if (Object.keys(avgByFn).length > 0) {
    checkSpace(30);
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text('AVERAGE COMPLIANCE BY FRAMEWORK DOMAIN', margin, y);
    y += 5;

    fnOrder.forEach(fn => {
      checkSpace(8);
      const val = avgByFn[fn] || 0;
      doc.setFontSize(8);
      doc.setTextColor(71, 85, 105);
      doc.text(fn, margin, y + 4);
      // Background bar
      doc.setFillColor(241, 245, 249);
      doc.roundedRect(margin + 28, y, contentW - 42, 5, 1, 1, 'F');
      // Fill bar
      const barColor = val >= 75 ? [34, 197, 94] : val >= 50 ? [234, 179, 8] : [239, 68, 68];
      doc.setFillColor(...barColor);
      doc.roundedRect(margin + 28, y, Math.max((val / 100) * (contentW - 42), 1), 5, 1, 1, 'F');
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      doc.text(`${val}%`, pageW - margin, y + 4, { align: 'right' });
      y += 8;
    });
    y += 4;
  }

  // Per-OES posture table
  if ((data.oes_posture_data || []).length > 0) {
    checkSpace(20);
    autoTable(doc, {
      startY: y,
      head: [[`${L.entityName} Name`, 'Overall', ...fnOrder]],
      body: data.oes_posture_data.map(r => [
        r.oes_name,
        `${r.overall_compliance}%`,
        ...fnOrder.map(fn => `${r.by_function?.[fn] ?? '—'}%`),
      ]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [241, 245, 249], textColor: [71, 85, 105], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 1: { fontStyle: 'bold' } },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── 4. Risk Assessment ──────────────────────────────────────────────
  sectionTitle('Risk Assessment');

  // Risk KPIs
  checkSpace(22);
  const riskKpiW = (contentW - 6) / 3;
  kpiBox('Max Risk Score', data.max_risk_score || 0, margin, riskKpiW);
  kpiBox('Avg Risk Score', data.average_risk_score || 0, margin + riskKpiW + 3, riskKpiW);
  kpiBox('Top Threats', (data.top_threats || []).length, margin + (riskKpiW + 3) * 2, riskKpiW);
  y += 20;

  // Risk distribution
  if ((data.risk_distribution || []).length > 0) {
    checkSpace(20);
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text('RISK CLASSIFICATION DISTRIBUTION', margin, y);
    y += 5;

    data.risk_distribution.forEach(d => {
      checkSpace(8);
      const color = hexToRgb(RISK_HEX[d.classification] || '#94a3b8');
      const maxCount = Math.max(...data.risk_distribution.map(x => x.count), 1);
      const barW = (d.count / maxCount) * (contentW - 45);

      doc.setFontSize(8);
      doc.setTextColor(71, 85, 105);
      doc.text(d.classification, margin, y + 4);
      doc.setFillColor(...color);
      doc.roundedRect(margin + 35, y, Math.max(barW, 2), 5, 1, 1, 'F');
      doc.setFontSize(7);
      doc.setTextColor(100, 116, 139);
      doc.text(String(d.count), margin + 38 + barW, y + 4);
      y += 8;
    });
    y += 4;
  }

  // Top threats table
  if ((data.top_threats || []).length > 0) {
    checkSpace(20);
    autoTable(doc, {
      startY: y,
      head: [['Threat', 'Max Risk Score']],
      body: data.top_threats.map(t => [t.threat_name, t.max_risk?.toFixed(1) || '0']),
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, cellPadding: 2 },
      headStyles: { fillColor: [241, 245, 249], textColor: [71, 85, 105], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // Top vulnerabilities table
  if ((data.top_vulnerabilities || []).length > 0) {
    checkSpace(20);
    autoTable(doc, {
      startY: y,
      head: [['Vulnerability', 'Classification', 'Risk Score']],
      body: data.top_vulnerabilities.map(v => [v.title, v.classification, v.risk_x_asset?.toFixed(1) || '0']),
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, cellPadding: 2 },
      headStyles: { fillColor: [241, 245, 249], textColor: [71, 85, 105], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      didParseCell: (hookData) => {
        if (hookData.section === 'body' && hookData.column.index === 1) {
          const cls = hookData.cell.raw;
          const hex = RISK_HEX[cls];
          if (hex) hookData.cell.styles.textColor = hexToRgb(hex);
        }
      },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // Asset exposure table
  if ((data.asset_exposure || []).length > 0) {
    checkSpace(20);
    autoTable(doc, {
      startY: y,
      head: [['Asset', 'Max Risk Score']],
      body: data.asset_exposure.map(a => [a.asset_name, a.max_risk?.toFixed(1) || '0']),
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, cellPadding: 2 },
      headStyles: { fillColor: [241, 245, 249], textColor: [71, 85, 105], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // ── Finalize ────────────────────────────────────────────────────────
  // Update footer on all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const footY = pageH - 10;
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.text(`${org.name || L.headerTitle} — Confidential`, margin, footY);
    doc.text(`Page ${i} of ${totalPages}`, pageW - margin, footY, { align: 'right' });
    doc.text(`Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageW / 2, footY, { align: 'center' });
  }

  // Download
  const filename = data.assessment_name
    ? `${L.reportFilenamePrefix}_Report_${data.assessment_name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
    : `${L.reportFilenamePrefix}_Assessment_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}
